import React, { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { subscribeToPush, unsubscribeFromPush, isPushSupported, getNotificationPermission } from '../services/pushService';

const DISMISSED_KEY = 'push_banner_dismissed';
const SUBSCRIBED_BANNER_KEY = 'push_subscribed_banner_dismissed';

export default function PushNotificationBanner() {
  const [visible, setVisible] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [subscribedBannerVisible, setSubscribedBannerVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    const perm = getNotificationPermission();
    if (perm === 'granted') {
      // Re-save subscription on load and only show "Enabled" if it actually succeeds
      subscribeToPush().then((ok) => {
        if (ok) {
          setSubscribed(true);
          if (!sessionStorage.getItem(SUBSCRIBED_BANNER_KEY)) {
            setSubscribedBannerVisible(true);
            const t = setTimeout(() => {
              setSubscribedBannerVisible(false);
              sessionStorage.setItem(SUBSCRIBED_BANNER_KEY, '1');
            }, 4000);
            return () => clearTimeout(t);
          }
        }
        // If subscribe failed silently, show the enable prompt again
      }).catch(() => {});
      return;
    }
    if (perm === 'denied') return;
    if (!sessionStorage.getItem(DISMISSED_KEY)) {
      setVisible(true);
    }
  }, []);

  const handleEnable = async () => {
    setLoading(true);
    const perm = await Notification.requestPermission();
    if (perm === 'granted') {
      const ok = await subscribeToPush();
      setSubscribed(ok);
      setVisible(false);
      if (ok) {
        setSubscribedBannerVisible(true);
        setTimeout(() => setSubscribedBannerVisible(false), 4000);
      }
    } else {
      setVisible(false);
    }
    setLoading(false);
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
  };

  const handleDisable = async () => {
    setLoading(true);
    await unsubscribeFromPush();
    setSubscribed(false);
    setSubscribedBannerVisible(false);
    setLoading(false);
  };

  // "Enabled" confirmation banner — auto-hides after 4s, has X to close early
  if (subscribed && subscribedBannerVisible) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-emerald-50 border-b border-emerald-200 px-4 py-2 flex items-center justify-between text-xs text-emerald-700">
        <span className="flex items-center gap-1.5"><Bell className="w-3.5 h-3.5" /> Push notifications enabled</span>
        <div className="flex items-center gap-3">
          <button onClick={handleDisable} disabled={loading} className="text-emerald-500 hover:text-red-500 underline">
            Disable
          </button>
          <button onClick={() => { setSubscribedBannerVisible(false); sessionStorage.setItem(SUBSCRIBED_BANNER_KEY, '1'); }} className="text-emerald-400 hover:text-emerald-600">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  if (!visible) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center justify-between gap-3 shadow-sm">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <Bell className="w-4 h-4 text-amber-600 flex-shrink-0" />
        <span className="text-xs text-amber-800">
          Enable notifications to get alerts for follow-ups, sessions &amp; stock updates
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleEnable}
          disabled={loading}
          data-testid="enable-push-btn"
          className="text-xs font-semibold bg-amber-600 text-white px-3 py-1 rounded-full hover:bg-amber-700 transition-colors disabled:opacity-50"
        >
          {loading ? 'Enabling…' : 'Enable'}
        </button>
        <button onClick={handleDismiss} className="text-amber-400 hover:text-amber-600">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
